import type { Embudo, Etapa, FaseAntigua, Negocio } from "./tipos";

/** Cómo se llega normalmente a la etapa (todas admiten el cambio a mano salvo Arranque). */
export type Movimiento = "botones" | "presentacion" | "manual" | "automatica";
/** Quién la trabaja: el comercial, Jan y Robert (ejecución) o los dos. */
export type Quien = "comercial" | "ejecucion" | "ambos";

export type DefinicionEtapa = {
  clave: Etapa;
  embudo: Embudo;
  orden: number;
  movimiento: Movimiento;
  quien: Quien;
  aparcada: boolean;
  /** Vuelta de Largo plazo / Reactivar: si se aparcó desde aquí, vuelve a esta (null = a sí misma). */
  vuelta_a: Etapa | null;
};

/**
 * Catálogo cerrado de etapas (Robert, 02/10/2026; docs/propuesta-embudos.md §10). Espejo
 * EXACTO de la tabla public.etapas (migración S8, la 34 y la 36): un test compara los dos. Los
 * nombres visibles están en i18n (`nevora.etapas.<clave>`).
 */
export const ETAPAS: DefinicionEtapa[] = [
  {
    clave: "nuevo",
    embudo: "ventas",
    orden: 1,
    movimiento: "botones",
    quien: "comercial",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "en_conversacion",
    embudo: "ventas",
    orden: 2,
    movimiento: "botones",
    quien: "comercial",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "consultoria_agendada",
    embudo: "ventas",
    orden: 3,
    movimiento: "botones",
    quien: "comercial",
    aparcada: false,
    vuelta_a: null,
  },
  {
    // Robert, 20/10/2026 (migración 36): el cliente no se presentó a la consultoría; se entra con
    // «No se presentó» y la ficha sigue siendo del setter, que la reagenda.
    clave: "no_show",
    embudo: "ventas",
    orden: 4,
    movimiento: "botones",
    quien: "comercial",
    aparcada: false,
    vuelta_a: null,
  },
  {
    // Robert, 19/10/2026 (migración 34): falta algo del cliente para preparar la propuesta.
    clave: "esperando_info",
    embudo: "ventas",
    orden: 5,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "preparando_propuesta",
    embudo: "ventas",
    orden: 6,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "propuesta_lista",
    embudo: "ventas",
    orden: 7,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "presentacion_agendada",
    embudo: "ventas",
    orden: 8,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: "pensandolo",
  },
  {
    clave: "pensandolo",
    embudo: "ventas",
    orden: 9,
    movimiento: "presentacion",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "aceptada",
    embudo: "ventas",
    orden: 10,
    movimiento: "presentacion",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "cobrado",
    embudo: "ventas",
    orden: 11,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "no_contesta",
    embudo: "ventas",
    orden: 12,
    movimiento: "botones",
    quien: "comercial",
    aparcada: true,
    vuelta_a: null,
  },
  {
    clave: "no_interesado",
    embudo: "ventas",
    orden: 13,
    movimiento: "botones",
    quien: "ambos",
    aparcada: true,
    vuelta_a: null,
  },
  {
    clave: "largo_plazo",
    embudo: "ventas",
    orden: 14,
    movimiento: "botones",
    quien: "comercial",
    aparcada: true,
    vuelta_a: null,
  },
  {
    clave: "arranque",
    embudo: "clientes",
    orden: 1,
    movimiento: "automatica",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "desarrollo",
    embudo: "clientes",
    orden: 2,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "produccion",
    embudo: "clientes",
    orden: 3,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "mantenimiento",
    embudo: "clientes",
    orden: 4,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: false,
    vuelta_a: null,
  },
  {
    clave: "baja",
    embudo: "clientes",
    orden: 5,
    movimiento: "manual",
    quien: "ejecucion",
    aparcada: true,
    vuelta_a: null,
  },
];

const POR_CLAVE = new Map<string, DefinicionEtapa>(
  ETAPAS.map((e) => [e.clave, e]),
);

export const EMBUDOS: Embudo[] = ["ventas", "clientes"];

export const esEtapa = (s: unknown): s is Etapa =>
  typeof s === "string" && POR_CLAVE.has(s);

export const definicionEtapa = (e: Etapa): DefinicionEtapa => {
  const d = POR_CLAVE.get(e);
  if (!d) throw new Error(`Etapa desconocida: ${e}`);
  return d;
};

export const embudoDe = (e: Etapa): Embudo => definicionEtapa(e).embudo;
export const esAparcada = (e: Etapa) => definicionEtapa(e).aparcada;

/** Etapas de un embudo en su orden (con las aparcadas al final). */
export const etapasDe = (embudo: Embudo): Etapa[] =>
  ETAPAS.filter((e) => e.embudo === embudo)
    .sort((a, b) => a.orden - b.orden)
    .map((e) => e.clave);

/**
 * Columnas del tablero de cada embudo, sin aparcadas. Ventas empieza en «En conversación»:
 * «Lead nuevo» vive en la pantalla Leads (pendientes de llamar).
 */
export const columnasTablero = (embudo: Embudo): Etapa[] =>
  etapasDe(embudo).filter((e) => !esAparcada(e) && e !== "nuevo");

export const aparcadasDe = (embudo: Embudo): Etapa[] =>
  etapasDe(embudo).filter(esAparcada);

/** Etapas del comercial: los botones de contacto mueven la tarjeta. */
export const ETAPAS_COMERCIAL: Etapa[] = [
  "nuevo",
  "en_conversacion",
  "consultoria_agendada",
  "no_show",
];

/** Etapas con «Resultado de la presentación». */
export const ETAPAS_PRESENTACION: Etapa[] = [
  "presentacion_agendada",
  "pensandolo",
];

/** «No contesta» aparca al llegar a los intentos de Ajustes solo en estas. */
export const ETAPAS_QUE_APARCAN: Etapa[] = ["nuevo", "en_conversacion"];

/** Llevan semáforo las de Ventas en curso (de Lead nuevo a Aceptada). */
export const esActiva = (e: Etapa) =>
  embudoDe(e) === "ventas" && !esAparcada(e) && e !== "cobrado";

/**
 * Qué hacen los botones «He llamado / He visitado / He escrito» en este proyecto: en las
 * etapas del comercial (y en las aparcadas desde ellas) registran un resultado y mueven
 * la tarjeta; de «Preparando propuesta» en adelante y en Clientes solo anotan el contacto
 * y el siguiente paso (Robert, 02/10/2026). Misma regla que registrar_resultado (S8).
 */
export const modoContacto = (
  p: Pick<Negocio, "embudo" | "etapa" | "etapa_antes_de_aparcar">,
): "resultado" | "anotar" => {
  if (p.embudo !== "ventas") return "anotar";
  if (ETAPAS_COMERCIAL.includes(p.etapa)) return "resultado";
  if (
    esAparcada(p.etapa) &&
    ETAPAS_COMERCIAL.includes(p.etapa_antes_de_aparcar ?? "nuevo")
  ) {
    return "resultado";
  }
  return "anotar";
};

export const tienePresentacion = (p: Pick<Negocio, "etapa">) =>
  ETAPAS_PRESENTACION.includes(p.etapa);

/**
 * Etapa a la que vuelve un proyecto aparcado (Largo plazo el día de vuelta, «Reactivar»):
 * la etapa desde la que se aparcó o su vuelta_a (Presentación agendada → Se lo tiene que
 * pensar). Si no se sabe, `porDefecto`. Espejo de nevora_etapa_de_vuelta (S8).
 */
export const etapaDeVuelta = (
  antes: Etapa | null | undefined,
  porDefecto: Etapa,
): Etapa => {
  const d = antes ? POR_CLAVE.get(antes) : undefined;
  if (!d || d.aparcada || d.clave === "cobrado" || d.embudo !== "ventas") {
    return porDefecto;
  }
  return d.vuelta_a ?? d.clave;
};

/** Dato que hace falta para entrar en una etapa a mano (arrastrar o menú de la ficha). */
export type DatoDeEtapa =
  | "fecha_reunion"
  | "fecha_presentacion"
  | "fecha_vuelta"
  | "importe_total"
  | "cobro";

export const datoQuePideEtapa = (e: Etapa): DatoDeEtapa | null => {
  switch (e) {
    case "consultoria_agendada":
      return "fecha_reunion";
    case "presentacion_agendada":
      return "fecha_presentacion";
    case "largo_plazo":
      return "fecha_vuelta";
    // 39 (Robert, 23/10/2026): sin importe no se acepta (la base tampoco lo deja).
    case "aceptada":
      return "importe_total";
    case "cobrado":
      return "cobro";
    default:
      return null;
  }
};

/** Error de un cambio de etapa, con clave i18n (`nevora.etapas_errores.*`). */
export class ErrorEtapa extends Error {
  constructor(public readonly clave: string) {
    super(clave);
    this.name = "ErrorEtapa";
  }
}

const APARCADAS_DEL_COMERCIAL: Etapa[] = [
  "no_contesta",
  "no_interesado",
  "largo_plazo",
];

/**
 * Etapas que mueve un setter (S10): Lead nuevo, En conversación, Consultoría agendada y las
 * aparcadas desde ellas. De la consultoría en adelante, y en Clientes, solo Jan y Robert.
 * Espejo de nevora_etapa_del_comercial (trigger proyectos_candado), que es quien manda.
 */
export const esEtapaDelComercial = (
  etapa: Etapa,
  antes?: Etapa | null,
): boolean =>
  ETAPAS_COMERCIAL.includes(etapa) ||
  (APARCADAS_DEL_COMERCIAL.includes(etapa) &&
    (antes == null || ETAPAS_COMERCIAL.includes(antes)));

/** ¿Puede este usuario tocar la tarjeta? Un administrador, siempre; un setter, en sus etapas. */
export const puedeTocarTarjeta = (
  p: Pick<Negocio, "embudo" | "etapa" | "etapa_antes_de_aparcar">,
  esAdmin: boolean,
): boolean =>
  esAdmin ||
  (p.embudo === "ventas" &&
    esEtapaDelComercial(p.etapa, p.etapa_antes_de_aparcar));

/**
 * Cambio de etapa a mano (arrastrar en el tablero, menú o selector de la ficha). Mismas
 * reglas que los triggers proyectos_antes (S8) y proyectos_candado (S10), que son quienes
 * mandan:
 *  - no se cambia de embudo a mano (a Clientes solo registrando el cobro; de Clientes no
 *    se vuelve a Ventas: compra nueva = proyecto nuevo);
 *  - con «no volver a contactar» no se reactiva una aparcada (decisión 5, 25/09/2026);
 *  - a Cobrado solo se llega registrando el cobro (importe y fecha);
 *  - un setter solo mueve sus etapas (S10).
 */
export const validarCambioEtapa = (
  p: Pick<Negocio, "embudo" | "etapa" | "no_llamar"> &
    Partial<Pick<Negocio, "etapa_antes_de_aparcar">>,
  destino: Etapa,
  esAdmin = true,
): void => {
  if (destino === p.etapa) return;
  if (
    !esAdmin &&
    (!puedeTocarTarjeta(
      { ...p, etapa_antes_de_aparcar: p.etapa_antes_de_aparcar ?? null },
      false,
    ) ||
      !esEtapaDelComercial(destino, esAparcada(p.etapa) ? null : p.etapa))
  ) {
    throw new ErrorEtapa("nevora.etapas_errores.solo_ejecucion");
  }
  if (embudoDe(destino) !== p.embudo) {
    throw new ErrorEtapa(
      p.embudo === "clientes"
        ? "nevora.etapas_errores.clientes_a_ventas"
        : "nevora.etapas_errores.ventas_a_clientes",
    );
  }
  if (esAparcada(p.etapa) && !esAparcada(destino) && p.no_llamar) {
    throw new ErrorEtapa("nevora.etapas_errores.no_llamar");
  }
  if (destino === "cobrado") {
    throw new ErrorEtapa("nevora.etapas_errores.cobrado_sin_cobro");
  }
  // 36: a No show solo con «No se presentó» (cuenta las veces; a la tercera, No interesado).
  if (destino === "no_show") {
    throw new ErrorEtapa("nevora.etapas_errores.no_show_con_boton");
  }
};

/**
 * Etapas que se eligen a mano en la ficha: las de su embudo, sin Cobrado (va con el cobro) ni
 * No show (va con «No se presentó»).
 * Un setter, solo las suyas.
 */
export const etapasElegibles = (embudo: Embudo, esAdmin = true): Etapa[] =>
  etapasDe(embudo).filter(
    (e) =>
      e !== "cobrado" &&
      e !== "no_show" &&
      (esAdmin || esEtapaDelComercial(e, null)),
  );

/**
 * Etapas que añade una migración posterior a la S8 (34: «Esperando info del cliente», 19/10/2026;
 * 36: «No show», 20/10/2026).
 * Mientras la base no las tenga, la app no las enseña (tablero, ficha, alta): moverle una tarjeta
 * fallaría. Cuando estén en producción y en todas las bases de prueba, puede quedar vacía.
 */
export const ETAPAS_NUEVAS: Etapa[] = ["esperando_info", "no_show"];

/** Quita de `lista` las etapas nuevas que la base aún no tiene (`enLaBase`: las que sí). */
export const sinLasQueFaltan = (
  lista: Etapa[],
  enLaBase: readonly Etapa[] | undefined,
): Etapa[] =>
  lista.filter((e) => !ETAPAS_NUEVAS.includes(e) || !!enLaBase?.includes(e));

/** Etapas en las que puede nacer una ficha nueva (alta a mano): Ventas en curso (sin No show). */
export const ETAPAS_ALTA: Etapa[] = etapasDe("ventas").filter(
  (e) => !esAparcada(e) && e !== "cobrado" && e !== "no_show",
);

/** Fases anteriores a S8 (notas antiguas, carga inicial, altas de Prospect hoy). */
export const FASES_ANTIGUAS: FaseAntigua[] = [
  "nuevo",
  "seguimiento",
  "reunion_agendada",
  "propuesta_enviada",
  "firmado",
  "cobrado",
  "no_contesta",
  "no_interesado",
  "largo_plazo",
];

/** Tabla de equivalencias de §10 (espejo de nevora_etapa_de_fase). */
export const etapaDeFaseAntigua = (f: FaseAntigua): Etapa => {
  switch (f) {
    case "seguimiento":
      return "en_conversacion";
    case "reunion_agendada":
      return "consultoria_agendada";
    case "propuesta_enviada":
      return "pensandolo";
    case "firmado":
      return "aceptada";
    case "cobrado":
      return "arranque";
    default:
      return f;
  }
};

export const claveEtapa = (e: Etapa) => `nevora.etapas.${e}`;
export const claveEmbudo = (e: Embudo) => `nevora.embudos.${e}`;

/** Etiqueta de una etapa o, en el historial anterior a S8, de una fase antigua. */
export const claveEtapaOFase = (s: string) =>
  esEtapa(s) ? claveEtapa(s) : `nevora.fases.${s}`;
